import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FilterNode, Localized } from '@qronstat/shared';
import { api } from './client';

export interface GroupType {
  id: string;
  code: string;
  name: Localized;
  objectType: string;
}

export interface GroupItem {
  id: string;
  name: string;
  description: string | null;
  kind: 'manual' | 'rule';
  rule: { filters: FilterNode[] } | null;
  typeCode: string;
  typeName: Localized;
  objectType: string;
  manualMembers: number;
  createdAt: string;
}

export interface GroupDetail extends Omit<GroupItem, 'manualMembers' | 'createdAt'> {
  members: { objectId: string; validFrom: string; validTo: string | null }[];
}

export interface AlertEventItem {
  id: string;
  ruleId: string | null;
  systemCode: string | null;
  ruleName: string | null;
  template: string | null;
  kind: 'fire' | 'recover';
  objectType: string | null;
  objectId: string | null;
  objectLabel: string | null;
  value: number | null;
  threshold: number | null;
  params: Record<string, unknown>;
  createdAt: string;
  read: boolean;
}

export function useGroupTypes() {
  return useQuery({
    queryKey: ['group-types'],
    queryFn: () => api<{ items: GroupType[] }>('/api/group-types'),
    staleTime: 5 * 60_000,
    meta: { persist: true },
  });
}

export function useGroups(typeCode?: string | null) {
  return useQuery({
    queryKey: ['groups', typeCode ?? 'all'],
    queryFn: () =>
      api<{ items: GroupItem[] }>(
        `/api/groups${typeCode ? `?typeCode=${encodeURIComponent(typeCode)}` : ''}`,
      ),
    staleTime: 60_000,
    meta: { persist: true },
  });
}

export function useGroup(id: string | null) {
  return useQuery({
    queryKey: ['group', id],
    queryFn: () => api<GroupDetail>(`/api/groups/${id}`),
    enabled: Boolean(id),
    staleTime: 60_000,
    meta: { persist: true },
  });
}

export function useAlertsFeed(limit: number, enabled = true) {
  return useQuery({
    queryKey: ['alerts-feed', limit],
    queryFn: () => api<{ items: AlertEventItem[] }>(`/api/alerts/feed?limit=${limit}`),
    enabled,
    staleTime: 20_000,
    refetchInterval: 60_000,
    meta: { persist: true },
  });
}

export function useUnreadAlerts(enabled: boolean) {
  return useQuery({
    queryKey: ['alerts-unread'],
    queryFn: () => api<{ count: number }>('/api/alerts/unread'),
    enabled,
    staleTime: 20_000,
    refetchInterval: 60_000,
  });
}

export function useMarkAlertsRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[] | 'all') =>
      api('/api/alerts/read', { method: 'POST', body: ids === 'all' ? { all: true } : { ids } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['alerts-feed'] });
      void client.invalidateQueries({ queryKey: ['alerts-unread'] });
    },
  });
}
